import { useCallback } from "react";
import { supabase } from "../../../supabaseClient";

/**
 * Hook to manage all event logic for multiple bowlers
 * - Loads all event status for bowlers
 * - Provides auto-selection logic
 * - Handles status updates
 */
export const useAllEventManagement = (
  tournamentID,
  allEventConfigs,
  selectedEvent,
  setBowlers
) => {
  /**
   * Load all event status for a single bowler
   * Returns: { hasEnteredAllEvents, primaryEventsEntered, firstAttemptsUsed }
   */
  const loadBowlerAllEventStatus = useCallback(
    async (bowlerId) => {
      if (!bowlerId || !tournamentID || !allEventConfigs.length) {
        return null;
      }

      try {
        // Get all event entries for this bowler, excluding entries from deleted teams
        // Also get the team's primary event (eventsID) to properly match all event entries to primary events
        const { data: eventEntries, error: entriesError } = await supabase
          .from("eventEntry")
          .select(
            `
            id,
            eventsID,
            teamID,
            tournamentEvents!inner (
              id,
              isPrimary,
              tournamentID
            ),
            teams!inner (
              id,
              eventsID,
              isDeleted
            )
          `
          )
          .eq("bowlerID", bowlerId)
          .eq("tournamentEvents.tournamentID", tournamentID)
          .eq("tournamentEvents.isDeleted", false)
          .eq("teams.isDeleted", false);

        if (entriesError) {
          console.error(`Error loading entries for bowler ${bowlerId}:`, entriesError);
          return null;
        }

        // Separate primary events and all events
        // Track team IDs for all event entries to support edit mode
        // Also track which primary events were used for which teams
        const primaryEventIds = new Set();
        const allEventIds = new Set();
        const allEventEntriesByTeam = {}; // Map of allEventId -> Set of teamIds
        const primaryEventByTeam = {}; // Map of primaryEventId -> Set of teamIds (for tracking which teams used which primary events)

        (eventEntries || []).forEach((entry) => {
          const event = entry.tournamentEvents;
          const team = entry.teams;
          if (!event || !team) return;

          if (event.isPrimary) {
            primaryEventIds.add(event.id);
            // Track which teams this primary event was used for
            if (!primaryEventByTeam[event.id]) {
              primaryEventByTeam[event.id] = new Set();
            }
            primaryEventByTeam[event.id].add(team.id);
          } else {
            const isAllEvent = allEventConfigs.some(
              (config) => config.allEventId === event.id
            );
            if (isAllEvent) {
              allEventIds.add(event.id);
              // Track which teams this all event entry is for
              if (!allEventEntriesByTeam[event.id]) {
                allEventEntriesByTeam[event.id] = new Set();
              }
              allEventEntriesByTeam[event.id].add(team.id);
            }
          }
        });

        // For each all event config, determine which primary events have been used
        // Track which primary events were used for which teams (for all event)
        const firstAttemptsUsed = {}; // Map of allEventId -> Set of primaryEventIds
        const firstAttemptsUsedByTeam = {}; // Map of allEventId -> Map of primaryEventId -> Set of teamIds
        const hasEnteredAllEvents = {};

        allEventConfigs.forEach((config) => {
          hasEnteredAllEvents[config.allEventId] = allEventIds.has(
            config.allEventId
          );

          // Find which primary events from this all event the bowler has entered
          // BUT only count them as "used" if they actually joined the all event
          // If they entered a primary event but didn't join all event, it doesn't count as "used"
          if (allEventIds.has(config.allEventId)) {
            // They joined this all event, so find which primary events they entered
            // AND track which teams those primary events were used for
            // IMPORTANT: Match all event entries to primary events by team's primary event
            const usedPrimaryEvents = new Set();
            const usedByTeam = {};
            
            // For each all event entry, find the team's primary event and mark it as used
            const allEventEntriesForThisAllEvent = (eventEntries || []).filter(entry => {
              const event = entry.tournamentEvents;
              return event && !event.isPrimary && event.id === config.allEventId;
            });
            
            allEventEntriesForThisAllEvent.forEach((entry) => {
              const team = entry.teams;
              if (!team) return;
              
              // Get the team's primary event
              const teamPrimaryEventId = team.eventsID;
              
              // Only count if this team's primary event is part of the all event config
              if (teamPrimaryEventId && config.primaryEventIds.includes(teamPrimaryEventId)) {
                usedPrimaryEvents.add(teamPrimaryEventId);
                
                // Track which team used this primary event
                if (!usedByTeam[teamPrimaryEventId]) {
                  usedByTeam[teamPrimaryEventId] = new Set();
                }
                usedByTeam[teamPrimaryEventId].add(team.id);
              }
            });
            
            firstAttemptsUsed[config.allEventId] = usedPrimaryEvents;
            firstAttemptsUsedByTeam[config.allEventId] = usedByTeam;
          } else {
            // They didn't join this all event, so no primary events are "used" for it
            firstAttemptsUsed[config.allEventId] = new Set();
            firstAttemptsUsedByTeam[config.allEventId] = {};
          }
        });

        return {
          hasEnteredAllEvents,
          primaryEventsEntered: Array.from(primaryEventIds),
          firstAttemptsUsed,
          firstAttemptsUsedByTeam, // Track which teams used which primary events for all event
          allEventEntriesByTeam, // Include team information for edit mode
        };
      } catch (error) {
        console.error(`Error loading all event status for bowler ${bowlerId}:`, error);
        return null;
      }
    },
    [tournamentID, allEventConfigs]
  );

  /**
   * Auto-select all events for a bowler based on logic (Case 3)
   * Only auto-selects if bowler has ALREADY entered the all event
   * AND this is a NEW primary event type they haven't entered yet
   */
  const autoSelectAllEventsForBowler = useCallback(
    async (bowlerId, bowlerIndex) => {
      if (!selectedEvent || !selectedEvent.isPrimary || !allEventConfigs.length || !tournamentID) {
        return;
      }

      try {
        // Get bowler's existing event entries, excluding entries from deleted teams
        const { data: eventEntries, error: entriesError } = await supabase
          .from("eventEntry")
          .select(
            `
            eventsID,
            tournamentEvents!inner (
              id,
              isPrimary,
              tournamentID
            ),
            teams!inner (
              id,
              isDeleted
            )
          `
          )
          .eq("bowlerID", bowlerId)
          .eq("tournamentEvents.tournamentID", tournamentID)
          .eq("tournamentEvents.isDeleted", false)
          .eq("teams.isDeleted", false);

        if (entriesError) {
          console.error("Error checking bowler entries:", entriesError);
          return;
        }

        // Separate primary events and all events
        const primaryEventIds = new Set();
        const allEventIds = new Set();

        (eventEntries || []).forEach((entry) => {
          const event = entry.tournamentEvents;
          if (!event) return;

          if (event.isPrimary) {
            primaryEventIds.add(event.id);
          } else {
            const isAllEvent = allEventConfigs.some(
              (config) => config.allEventId === event.id
            );
            if (isAllEvent) {
              allEventIds.add(event.id);
            }
          }
        });

        // Determine which all events to auto-select
        // Case 3: Only auto-select if bowler has ALREADY entered the all event
        // AND this is a NEW primary event type they haven't entered yet
        const allEventsToSelect = {};
        allEventConfigs.forEach((config) => {
          // Check if current primary event is part of this all event
          const isPrimaryInAllEvent = config.primaryEventIds.includes(
            selectedEvent.id
          );

          if (!isPrimaryInAllEvent) return;

          // Check if bowler has already entered this all event (paid)
          const hasEnteredAllEvent = allEventIds.has(config.allEventId);
          
          // Check if this is a new primary event type they haven't entered yet
          const hasUsedThisPrimary = primaryEventIds.has(selectedEvent.id);
          
          // Case 3: Auto-select only if:
          // 1. They've already entered the all event (paid)
          // 2. This is a NEW primary event type (haven't used first attempt yet)
          if (hasEnteredAllEvent && !hasUsedThisPrimary) {
            allEventsToSelect[config.allEventId] = true;
          }
          // All other cases: don't auto-select (optional or already used)
        });

        // Update bowler's selected side pots
        if (Object.keys(allEventsToSelect).length > 0) {
          setBowlers((prev) => {
            const updated = [...prev];
            updated[bowlerIndex] = {
              ...updated[bowlerIndex],
              selectedSidePots: {
                ...updated[bowlerIndex].selectedSidePots,
                ...allEventsToSelect,
              },
            };
            return updated;
          });
        }
      } catch (error) {
        console.error("Error auto-selecting all events:", error);
      }
    },
    [selectedEvent, allEventConfigs, tournamentID, setBowlers]
  );

  /**
   * Get all event status message and disabled state for a specific all event
   * Returns: { message: { type, text, color } | null, disabled: boolean }
   * 
   * This implements all 5 cases for all event entry logic:
   * - Case 1: First time entering any primary event
   * - Case 2: Entering same primary event again, DID join all event first time (HARD BLOCK)
   * - Case 3: Entering new primary event type after entering all event
   * - Case 4: Entering same primary event again, did NOT join all event first time
   * - Case 5: Entering new primary event type, did NOT join all event for previous event
   * 
   * Additional parameters for edit mode:
   * - currentTeamId: The team being edited (optional, for edit mode)
   * - isCurrentlySelected: Whether this all event is currently selected for this team (optional)
   */
  const getAllEventStatus = useCallback(
    (allEventId, allEventStatus, allEventConfigs, selectedEvent, currentTeamId = null, isCurrentlySelected = false) => {
      if (!allEventStatus || !allEventConfigs.length || !selectedEvent) {
        return { message: null, disabled: false };
      }

      const config = allEventConfigs.find((c) => c.allEventId === allEventId);
      if (!config) {
        return { message: null, disabled: false };
      }

      // Check if current primary event is part of this all event
      const isCurrentPrimaryInAllEvent = config.primaryEventIds.includes(selectedEvent.id);
      
      // Check if this primary event was used for the CURRENT team (not just any team)
      const firstAttemptsUsedByTeam = allEventStatus.firstAttemptsUsedByTeam || {};
      const primaryEventTeams = firstAttemptsUsedByTeam[allEventId]?.[selectedEvent.id];
      const hasUsedThisPrimaryForCurrentTeam = currentTeamId && 
        primaryEventTeams && 
        primaryEventTeams instanceof Set &&
        primaryEventTeams.has(currentTeamId);
      
      // Also check if it was used for ANY team (for backward compatibility and non-edit mode)
      const hasUsedThisPrimaryForAnyTeam =
        isCurrentPrimaryInAllEvent &&
        allEventStatus.firstAttemptsUsed[allEventId]?.has(selectedEvent.id);
      
      // For edit mode, we care about current team. For create mode, we care about any team.
      const hasUsedThisPrimary = currentTeamId 
        ? hasUsedThisPrimaryForCurrentTeam 
        : hasUsedThisPrimaryForAnyTeam;
      
      const hasEnteredAllEvent = allEventStatus.hasEnteredAllEvents[allEventId];
      const hasEnteredThisPrimary = allEventStatus.primaryEventsEntered.includes(selectedEvent.id);

      // EDIT MODE: Check if this all event entry exists for the current team
      // If it does, allow toggling (can remove it or it's already there)
      const allEventEntriesByTeam = allEventStatus.allEventEntriesByTeam || {};
      const teamsWithThisAllEvent = allEventEntriesByTeam[allEventId];
      
      // Handle Set format (teamsWithThisAllEvent is a Set of team IDs)
      const hasAllEventForCurrentTeam = currentTeamId && 
        teamsWithThisAllEvent && 
        teamsWithThisAllEvent instanceof Set &&
        teamsWithThisAllEvent.has(currentTeamId);

      // EDIT MODE: If this all event entry exists for this team, allow toggling
      // This allows removing or re-adding the all event entry when editing the team where it was entered
      // OR if it's currently selected in the UI (even if not yet saved), allow toggling
      // Check specifically if it was used for the CURRENT team (not any team)
      if (currentTeamId && hasUsedThisPrimaryForCurrentTeam && (hasAllEventForCurrentTeam || isCurrentlySelected)) {
        return {
          message: isCurrentlySelected ? null : {
            type: "currently_selected",
            text: "Can be re-added for this team",
            color: "text-gray-600",
          },
          disabled: false, // Allow toggling for this team
        };
      }

      // Case 2: Entering same primary event again - DISABLE (hard constraint)
      // Only disable if they DID join all event the first time (first attempt was used)
      // Check ANY team, not just current team - this is a hard block
      // The edit mode check above only allows toggling if it was used for the CURRENT team
      if (hasUsedThisPrimaryForAnyTeam) {
        return {
          message: {
            type: "already_counted",
            text: "Already has a score for this event",
            color: "text-gray-600",
          },
          disabled: true, // Hard constraint - can't count same primary event twice
        };
      }

      // Case 3: Entering new primary event type after entering all event
      if (
        hasEnteredAllEvent &&
        isCurrentPrimaryInAllEvent &&
        !hasUsedThisPrimaryForAnyTeam
      ) {
        return {
          message: {
            type: "will_count",
            text: "Already entered all event - this score will count towards all event",
            color: "text-brand-gold-ink",
          },
          disabled: false, // Admin can turn off
        };
      }

      // Case 4: Entering same primary event again, did NOT join all event first time
      // This covers when they've entered this primary event before but never joined all event
      // Check if bowler has ANY all event entries to determine the message
      const hasAnyAllEventEntries = Object.values(allEventStatus.hasEnteredAllEvents || {}).some(entered => entered === true);
      
      if (
        hasEnteredThisPrimary &&
        !hasEnteredAllEvent &&
        isCurrentPrimaryInAllEvent &&
        !hasUsedThisPrimaryForAnyTeam
      ) {
        return {
          message: {
            type: "no_all_event",
            text: hasAnyAllEventEntries 
              ? "Not first attempt at event" 
              : "No all event entry for prior attempts",
            color: "text-amber-600",
          },
          disabled: false, // Admin can override
        };
      }

      // Case 5: Entering new primary event type, did NOT join all event for previous event
      // Check if they entered any other primary event in this all event but didn't join all event
      // Must be a NEW primary event type (hasEnteredThisPrimary is false)
      if (
        !hasEnteredThisPrimary &&
        isCurrentPrimaryInAllEvent &&
        !hasEnteredAllEvent &&
        !hasUsedThisPrimaryForAnyTeam
      ) {
        const enteredOtherPrimaries = config.primaryEventIds.some(
          (primaryId) =>
            primaryId !== selectedEvent.id &&
            allEventStatus.primaryEventsEntered.includes(primaryId)
        );
        if (enteredOtherPrimaries) {
          return {
            message: {
              type: "no_all_event",
              text: "Already had first score for previous event and didn't join all event",
              color: "text-amber-600",
            },
            disabled: false, // Admin can override
          };
        }
      }

      // Case 1: First time entering any primary event - no message, not disabled
      return { message: null, disabled: false };
    },
    []
  );

  return {
    loadBowlerAllEventStatus,
    autoSelectAllEventsForBowler,
    getAllEventStatus,
  };
};

